import { type TypeTableColumn } from '@thefarmersfront/kitchen';

import { BadgeCell, TextButtonCell } from '~/components/common';

import { formatNumber } from '~/utils/common';
import { formatDate } from '~/utils/date';

import { DATE_FORMAT } from '~/constants';
import { CLAIM_RETURN_INBOUND_STATUS_BADGE_COLOR, CLAIM_RETURN_INSPECTION_STATUS_BADGE_COLOR } from '~/constants/claim';

import type { TypeReturnDetailData } from '~/types/claim.types';

type TypeReturnGoodsInspection = NonNullable<TypeReturnDetailData['returnGoodsInspections']>[number];
type TypeInspectionDetail = TypeReturnGoodsInspection['inspectionDetails'][number];

/** 검수 단위 1건당 1행. 상품 칸은 같은 상품의 행끼리 병합한다. 검수 단위가 없는 상품도 한 행으로 보인다. */
export type TypeReturnInspectionRow = Omit<TypeReturnGoodsInspection, 'inspectionDetails'> & {
  rowId: string;
  goodsIndex: number;
  inspection: TypeInspectionDetail | null;
};

export const flattenReturnInspections = (inspections: TypeReturnGoodsInspection[]): TypeReturnInspectionRow[] =>
  inspections.flatMap(({ inspectionDetails, ...goods }, goodsIndex) => {
    const details: (TypeInspectionDetail | null)[] = inspectionDetails?.length ? inspectionDetails : [null];
    return details.map((inspection, index) => ({ ...goods, rowId: `${goodsIndex}-${index}`, goodsIndex, inspection }));
  });

/**
 * [반품 신청/조회] 신청 상세 모달 > '검수 처리 상세' 테이블 컬럼 정의
 */
const getClaimReturnDetailInspectionColumnDefs = ({
  onClickViewInspectionImage,
}: {
  onClickViewInspectionImage: (imageList: string[]) => void;
}) => {
  const columnDefs: TypeTableColumn<TypeReturnInspectionRow>[] = [
    {
      id: 'no',
      accessorFn: (row) => row.goodsIndex + 1,
      header: 'No',
      size: 72,
      meta: { rowSpan: true, rowSpanOrder: 1 },
    },
    {
      accessorKey: 'goodsCode',
      header: '상품코드',
      size: 130,
      minSize: 130,
      meta: { flex: true, rowSpan: true },
    },
    {
      accessorKey: 'goodsName',
      header: '상품명',
      size: 260,
      meta: { rowSpan: true },
    },
    {
      accessorKey: 'returnQuantity',
      header: '반품신청수량',
      size: 100,
      cell: ({ getValue }) => {
        const value = getValue<number | null | undefined>();
        return value == null || Number.isNaN(value) ? '-' : formatNumber(value);
      },
      meta: { rowSpan: true },
    },
    {
      id: 'returnReason.name',
      accessorFn: (row) => row.returnReason?.name,
      header: '반품사유',
      size: 100,
      meta: { rowSpan: true },
    },
    {
      id: 'inspectionQuantity',
      accessorFn: (row) => row.inspection?.inspectionQuantity,
      header: '검수수량',
      size: 80,
      cell: ({ getValue }) => {
        const value = getValue<number | undefined>();
        return value ? formatNumber(value) : '-';
      },
    },
    {
      id: 'inboundStatus',
      accessorFn: (row) => row.inspection?.inboundStatus,
      header: '입하상태',
      size: 100,
      cell: ({ getValue }) => {
        const value = getValue<TypeInspectionDetail['inboundStatus'] | undefined>();
        if (!value) return '-';
        return (
          <BadgeCell
            value={value}
            colorMap={{ [value]: { label: value, color: CLAIM_RETURN_INBOUND_STATUS_BADGE_COLOR[value] } }}
            variant="light"
            status
          />
        );
      },
    },
    {
      id: 'inspectionStatus',
      accessorFn: (row) => row.inspection?.inspectionStatus,
      header: '검수상태',
      size: 100,
      cell: ({ getValue }) => {
        const value = getValue<string | undefined>();
        if (!value) return '-';
        const color =
          CLAIM_RETURN_INSPECTION_STATUS_BADGE_COLOR[value as keyof typeof CLAIM_RETURN_INSPECTION_STATUS_BADGE_COLOR];
        return <BadgeCell value={value} colorMap={{ [value]: { label: value, color } }} variant="light" status />;
      },
    },
    {
      id: 'inspectionImageUrls',
      accessorFn: (row) => row.inspection?.inspectionImageUrls,
      header: '검수이미지',
      size: 92,
      cell: ({ getValue }) => {
        const imageList = getValue<string[] | undefined>();
        return (
          <TextButtonCell
            value={imageList}
            variant="normal"
            onClick={() => {
              if (!imageList) return;
              onClickViewInspectionImage(imageList);
            }}
          >
            보기
          </TextButtonCell>
        );
      },
    },
    {
      id: 'inspectionAt',
      accessorFn: (row) => row.inspection?.inspectionAt,
      header: '검수일시',
      size: 167,
      cell: ({ getValue }) => formatDate(getValue<string | null | undefined>(), DATE_FORMAT.DATE_TIME) ?? '-',
    },
  ];

  return { columnDefs };
};

export default getClaimReturnDetailInspectionColumnDefs;
